import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("aestha_token"));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    has_materials_access: true,
    has_exams_access: true,
    has_clients_access: true,
    subscription_tier: "complete",
    is_admin: false,
    access_expired: false,
    days_remaining: null
  });

  useEffect(() => {
    // Fetch user data when token exists but user is not loaded
    if (token && !user) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      fetchUser();
    } else if (!token) {
      setLoading(false);
    }
  }, [token]); // Run when token changes

  const fetchUser = async () => {
    try {
      // First try to get user data
      const userResponse = await axios.get(`${API}/auth/me`);
      setUser(userResponse.data);
      
      // Then try to get permissions (don't fail if this doesn't work)
      try {
        const permResponse = await axios.get(`${API}/auth/permissions`);
        setPermissions(permResponse.data);
      } catch (permError) {
        console.error("Error fetching permissions:", permError);
        // Set default admin permissions if user is admin
        if (userResponse.data.role === "admin") {
          setPermissions({
            has_materials_access: true,
            has_exams_access: true,
            has_clients_access: true,
            subscription_tier: "complete",
            is_admin: true,
            access_expired: false,
            days_remaining: null
          });
        }
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      // Only logout if the main user request failed (invalid token)
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setLoading(true); // Start loading during login
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      localStorage.setItem("aestha_token", access_token);
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setToken(access_token);
      setUser(userData);
      
      // Fetch permissions after login
      try {
        const permResponse = await axios.get(`${API}/auth/permissions`);
        setPermissions(permResponse.data);
      } catch (e) {
        console.error("Error fetching permissions:", e);
      }
      
      return userData;
    } finally {
      setLoading(false); // End loading after login completes
    }
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${API}/auth/register`, { name, email, password });
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("aestha_token");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
    setPermissions({
      has_materials_access: true,
      has_exams_access: true,
      has_clients_access: true,
      subscription_tier: "complete",
      is_admin: false,
      access_expired: false,
      days_remaining: null
    });
  };

  // Helper functions
  const hasMaterialsAccess = () => permissions.has_materials_access;
  const hasExamsAccess = () => permissions.has_exams_access;
  const hasClientsAccess = () => permissions.has_clients_access;
  const isAccessExpired = () => permissions.access_expired;
  const getDaysRemaining = () => permissions.days_remaining;

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      permissions,
      hasMaterialsAccess,
      hasExamsAccess,
      hasClientsAccess,
      isAccessExpired,
      getDaysRemaining,
      refreshUser: fetchUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
